import classNames from "classnames";
import { massageImageUrl } from "common/components/portable-renderers";
import { browserOnPhone } from "lib/browser-detect";
import tw from "twin.macro";
import { css } from "webapp/styled-components";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { WireMenuButton } from "webapp/ui/views/wire-menu-button/wire-menu-button";
import { breakoutActions, makeActionSpreadProps } from "webapp/wire-renderers/wire-lib";
import { isBound, UIButtonAppearance, UIImageStyle, UIStyleVariant } from "wire/types";
import Img from "../img/img";
import type { CardProps } from "./card-collection-types";

export const Table: React.VFC<CardProps & { styleVariant: UIStyleVariant }> = p => {
    const { desc, imageStyle, styleVariant } = p;
    const [primary, secondary, ...menuActions] = breakoutActions(desc.menuItems, 2);
    const content = (
        <div
            onClick={e => {
                desc.onCardClick?.();
                e.stopPropagation();
            }}
            className={classNames(desc.onCardClick && "has-click-action", "group", styleVariant)}
            css={css`
                ${tw`px-4 border-solid border-border-base not-last:(border-b)`}

                &.${UIStyleVariant.Minimal} {
                    ${tw`bg-transparent px-2 first:border-t last:border-b`}
                }

                &.has-click-action {
                    ${tw`hover:(bg-bg-hovered cursor-pointer)`}
                }
            `}
            tw="relative bg-n0 height[52px] text-text-contextual-dark items-center flex w-full overflow-hidden
                transition-colors --button-opacity[0] hover:(--button-opacity[1]) focus-within:(--button-opacity[1])">
            {isBound(desc.image) && (
                <Img
                    src={massageImageUrl(desc.image, { thumbnail: true, width: 32, height: 32 }, undefined)}
                    tw="w-8 h-8 mr-4 flex-shrink-0 object-cover bg-n50 rounded-lg"
                    className={imageStyle}
                    css={css`
                        &.${UIImageStyle.Circle} {
                            ${tw`rounded-full`}
                        }
                    `}
                />
            )}
            <div
                tw="flex-1 grid grid-cols-3 gap-2 py-2 text-sm text-text-contextual-pale all-child:(overflow-hidden
                    whitespace-nowrap overflow-ellipsis)">
                <div tw="text-text-contextual-dark font-semibold">{desc.title}</div>
                <div>{desc.subtitle}</div>
                <div>{desc.emphasis}</div>
            </div>
            <div
                tw="flex transition-opacity ml-4 items-center"
                className={browserOnPhone ? "mobile" : undefined}
                css={css`
                    &.mobile {
                        opacity: 1;
                    }
                `}
                onClick={e => e.stopPropagation()}>
                {primary !== undefined && (
                    <WireButton
                        data-testid="card-primary-button"
                        appearance={browserOnPhone ? UIButtonAppearance.MobilePrimary : UIButtonAppearance.Filled}
                        {...makeActionSpreadProps(primary)}
                    />
                )}
                {secondary !== undefined && (
                    <WireButton
                        tw="not-first:ml-2"
                        appearance={browserOnPhone ? UIButtonAppearance.MobileSecondary : UIButtonAppearance.Bordered}
                        {...makeActionSpreadProps(secondary)}
                    />
                )}
                {menuActions.length > 0 && (
                    <WireMenuButton
                        tw="not-first:ml-2"
                        appearance={UIButtonAppearance.Bordered}
                        menuItems={menuActions}
                    />
                )}
            </div>
        </div>
    );
    return content;
};
